/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — logged-in user account/dashboard: upcoming
 *              events, packages ("pacchetti"), attendance management
 *   extends:   Controller
 *   template:  none
 *   qp:        ['redirect_id']
 *   key:       loadDataTask, nextEventTask, selectDateTask,
 *              segnaPresenzaTask (+ pendingPresencePax / pendingPresenceDate:
 *              the pick that shows the busy ring), removePresenceTask (tasks),
 *              logout (action)
 *   uses:      applicationController (app/controllers/application.js) —
 *              option_field.ecommerce_pacchetti_dispo, destroyRecord,
 *              logoutTask; services: intl, notify, router, session, store,
 *              log; beebee-base/lib/get-config,
 *              beebee-base/lib/utility/ajax; moment-timezone;
 *              e-commerce/lib/school-day (package day windows on Rome days)
 *   siblings:  routes/user.js
 *   tests:     tests/unit/controllers/user-test.js
 *
 * Logged-in user dashboard controller for the e-commerce storefront.
 */
import Controller from '@ember/controller';
import moment from 'moment-timezone';
import { schoolDay, schoolNow } from 'e-commerce/lib/school-day';

import { task } from 'ember-concurrency';

import { action, get, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { inject as controller } from '@ember/controller';

import { inject as service } from '@ember/service';

import getConfig from 'beebee-base/lib/get-config';
import { beebeeAjaxGet, beebeeAjaxPost } from 'beebee-base/lib/utility/ajax';

export default class UserController extends Controller {
  @controller('application') applicationController;
  @service intl;

  @service notify;
  @service router;
  @service session;
  @service store;
  @service log;

  queryParams = ['redirect_id'];

  @tracked redirect_id;

  @tracked content;
  @tracked payments;
  @tracked projects;
  @tracked events;

  // The pax + date being marked by segnaPresenzaTask: only THAT chip / row shows the busy ring (chips.md §2).
  @tracked pendingPresencePax = null;
  @tracked pendingPresenceDate = null;

  @task *loadDataTask() {
    yield this.nextEventTask.perform();
  }

  @task({ restartable: true }) *nextEventTask() {
    if (!this.session.isAuthenticated()) {
      return;
    }

    var self = this;

    try {
      yield this.session.reloadMeTask.perform();
      const me = this.session.me;

      if (!me?.id) {
        return;
      }

      var ecommerce_pacchetti_dispo = this.applicationController.get(
        'option_field.ecommerce_pacchetti_dispo'
      );

      const data = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/next_events/${me.get('id')}`,
        self.session.token
      );

      if (data?.[0]?.projects && Array.isArray(data[0].projects.all_projects)) {
        data[0].projects.all_projects.forEach(function (p) {
          //try {
          p.productObj = JSON.parse(p.product);
          //} catch(e) {}

          if (p.is_pacchetto) {
            if (!p.is_condivisa) {
              if (p.scianti && Array.isArray(p.scianti)) {
                p.scianti.forEach(function (s) {
                  const diffDay = parseInt(p.duration) - parseInt(s.related);

                  set(s, 'rimanenti', diffDay);
                  set(s, 'pkgOpen', diffDay > 0 ? true : false);

                  if (Array.isArray(s.related_projects)) {
                    s.related_projects.forEach(function (rp) {
                      const dateM = schoolDay(rp.start_date);
                      let canDelete = false;

                      const isAfter = ecommerce_pacchetti_dispo?.del_da_giorni
                        ? schoolNow().isSameOrAfter(
                            moment(dateM).subtract(ecommerce_pacchetti_dispo.del_da_giorni, 'day')
                          )
                        : false;
                      const isBefore = ecommerce_pacchetti_dispo?.del_a_giorni
                        ? schoolNow().isSameOrBefore(
                            moment(dateM).subtract(ecommerce_pacchetti_dispo.del_a_giorni, 'day')
                          )
                        : false;

                      if (schoolNow().isBefore(dateM, 'day') && isAfter && isBefore) {
                        canDelete = true;
                      }

                      set(rp, 'canDelete', canDelete);
                    });
                  }
                });
              }
            }
          }
        });
      }

      if (data[0].events && Array.isArray(data[0].events.all_events)) {
        data[0].events.all_events.forEach(function (p) {
          try {
            p.productObj = JSON.parse(p.product);
          } catch (e) {}
        });
      }

      this.content = me;
      this.payments = data[0].payments;
      this.projects = data[0].projects;
      this.events = data[0].events;
    } catch (e) {
      // Left uncaught, a 4xx/network failure here surfaced as a Sentry
      // unhandled rejection. Background loader: log only, no notify.
      this.log.warn('nextEventTask: /next_events failed', e);
    }
  }

  @task({ drop: true }) *selectDateTask(item, sciante) {
    var self = this,
      ecommerce_pacchetti_dispo = self.applicationController.get(
        'option_field.ecommerce_pacchetti_dispo'
      );

    set(sciante, 'dateCollettiva', []);

    /*let date = yield jQuery.ajax({
      cache: false,
      type: 'GET',
      beforeSend(xhr) {
        xhr.setRequestHeader('Authorization', `Bearer ${self.session.token}`);
      },
      xhrFields: {
        withCredentials: false,
      },
      data: {
        //'giorni': item,
        product_id: item.product_id,
        comprensorio: item.comprensorio,
      },
      dataType: 'json',
      url: `${getConfig(this).APP.urlApi}/scuolasci_date_collettiva`,
    });*/

    if (Array.isArray(item.days)) {
      const groupedItems = {};
      const arrGrouped = [];

      const startDate = schoolDay(item.start_date, 'YYYY-MM-DD');

      item.days.forEach(function (day) {
        const dateM = schoolDay(day, 'YYYY-MM-DD');

        const isAfter = ecommerce_pacchetti_dispo?.add_da_giorni
          ? schoolNow().isSameOrAfter(
              moment(dateM).subtract(ecommerce_pacchetti_dispo.add_da_giorni, 'days')
            )
          : false;

        const isBefore = ecommerce_pacchetti_dispo?.add_a_giorni
          ? schoolNow().isSameOrBefore(
              moment(dateM).subtract(ecommerce_pacchetti_dispo.add_a_giorni, 'days')
            )
          : false;

        if (isAfter && isBefore && dateM.isSameOrAfter(startDate, 'day')) {
          const monthKey = dateM.format('MM-YYYY');

          const isSelected =
            Array.isArray(sciante.related_projects) &&
            sciante.related_projects.some((rp) =>
              schoolDay(rp.start_date, 'YYYY-MM-DD').isSame(dateM, 'days')
            );

          if (get(groupedItems, monthKey)) {
            get(groupedItems, `${monthKey}.date`).push({
              isSelected: isSelected,
              item: day,
            });
          } else {
            const grItem = {
              month: dateM.startOf('month').add(1, 'day').format('YYYY-MM-DD'),
              date: [
                {
                  isSelected: isSelected,
                  item: day,
                },
              ],
            };

            set(groupedItems, monthKey, grItem);
            arrGrouped.push(grItem);
          }
        }
      });

      set(sciante, 'dateCollettiva', arrGrouped);
    }
  }

  @task({ drop: true }) *segnaPresenzaTask(item, sciante, date, operation = 'add') {
    var self = this;
    // set before the first yield: a dropped second perform never reaches here, so the ring stays on the running pick
    this.pendingPresencePax = sciante;
    this.pendingPresenceDate = date;

    try {
      yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/collettive/presenza`,
        self.session.token,
        {
          product: item.product_id,
          operation: operation,
          slot: [
            {
              data: date,
              bambino: sciante.id,
              rim: 0,
            },
          ],
        }
      );

      set(sciante, 'dateCollettiva', null);
      this.nextEventTask.perform();

      this.notify.success(this.intl.t('date saved'));
    } catch (e) {
      // Left uncaught, a 4xx/network failure here surfaced as a Sentry
      // unhandled rejection. User-triggered (mark attendance): notify.
      this.log.error('segnaPresenzaTask failed', e);
      const detail = e?.responseJSON?.errors?.[0]?.detail;
      this.notify.error(detail || this.intl.t('beebeeboard error'));
      return;
    }
  }

  @task *removePresenceTask(date) {
    const pr = yield this.store.findRecord('project', date.id, {
      reload: true,
    });

    yield this.applicationController.destroyRecord(pr);
    this.nextEventTask.perform();
  }

  @action
  logout(event) {
    event?.preventDefault?.();
    this.applicationController.logoutTask.perform();
  }
}
